/**
 * Kontoauszug abgleichen: Lader und Schreibwege (0070). Lesen über Tabellen mit RLS, schreiben nur über die Funktionen
 * bank_lines_import, bank_line_allocate, bank_line_ignore, bank_line_reopen und brand_payer_account_add.
 */
import type { Api } from "../../../data/api";
import type { BrandRow, InvoiceRow, TalentRow } from "../../../data/queries";
import type { AllocationDraft } from "@talentkontor/domain";
import type { BankLineRow, ImportLine, LinkedPaymentRow, PayerAccountRow } from "./kontoauszug-model";

export interface BankContext {
  lines: BankLineRow[];
  payerAccounts: PayerAccountRow[];
  payments: LinkedPaymentRow[];
  invoices: InvoiceRow[];
  brands: BrandRow[];
  talents: (TalentRow & { company_name?: string | null })[];
  talentHashes: { talent_id: string; iban_hash: string | null }[];
  settings: { bank_learn_payer_iban: boolean; bank_line_retention_days: number | null } | null;
}

/** Alles, was die Seite braucht; Prüfwerte der Creator-Konten rechnet die Datenbank (sonst ohne Kontoabgleich für Creator). */
export async function loadBankContext(api: Api, orgId: string): Promise<BankContext> {
  const byOrg = [["org_id", "eq", orgId]] as const;
  const [lines, payerAccounts, invoices, brands, talents, talentHashes, settings] = await Promise.all([
    api.select<BankLineRow>("bank_statement_lines", { filters: byOrg, order: [["booking_date", "desc"], ["imported_at", "desc"]] }),
    api.select<PayerAccountRow>("brand_payer_accounts", { filters: byOrg, order: [["created_at", "desc"]] }),
    api.select<InvoiceRow>("invoices", { filters: byOrg, order: [["issue_date", "desc"]] }),
    api.select<BrandRow>("brands", { filters: byOrg, order: [["name", "asc"]] }),
    api.select<TalentRow & { company_name?: string | null }>("talents", { filters: byOrg, order: [["first_name", "asc"]] }),
    api.read<{ talent_id: string; iban_hash: string | null }[]>("bank_talent_iban_hashes", { p_org: orgId }).catch(() => []),
    api.select<{ bank_learn_payer_iban: boolean; bank_line_retention_days: number | null }>("org_settings",
      { columns: "bank_learn_payer_iban, bank_line_retention_days", filters: byOrg }).then((r) => r[0] ?? null),
  ]);
  const matchedIds = lines.filter((l) => l.status === "matched").map((l) => l.id);
  const payments = matchedIds.length
    ? await api.select<LinkedPaymentRow>("invoice_payments", { columns: "id, invoice_id, bank_line_id, kind, amount_cents, currency", filters: [["bank_line_id", "in", matchedIds]] })
    : [];
  return { lines, payerAccounts, payments, invoices, brands, talents, talentHashes: talentHashes ?? [], settings };
}

export interface ImportResult {
  received: number;
  inserted: number;
  ids: string[];
  duplicates: { open: number; matched: number; ignored: number };
}

/** Eingänge übernehmen (in Paketen zu 500); schon importierte überspringt die Datenbank. */
export async function importLines(api: Api, orgId: string, source: BankLineRow["source"], fileName: string | null, ownAccountMasked: string | null, lines: readonly ImportLine[]): Promise<ImportResult> {
  const total: ImportResult = { received: 0, inserted: 0, ids: [], duplicates: { open: 0, matched: 0, ignored: 0 } };
  for (let i = 0; i < lines.length; i += 500) {
    const r = await api.rpc<ImportResult>("bank_lines_import", { p_org: orgId, p_source: source, p_file_name: fileName, p_own_account_masked: ownAccountMasked, p_lines: lines.slice(i, i + 500) });
    total.received += r.received;
    total.inserted += r.inserted;
    total.ids.push(...r.ids);
    total.duplicates.open += r.duplicates.open;
    total.duplicates.matched += r.duplicates.matched;
    total.duplicates.ignored += r.duplicates.ignored;
  }
  return total;
}

export interface AllocateResult {
  line_id: string;
  status: "matched";
  allocated_cents: number;
  rest_cents: number;
  payments: { invoice_id: string; number: string | null; status: string; open_cents: number; overpaid_cents: number; amount_cents: number }[];
  learned_brand: string | null;
}

export function allocateLine(api: Api, lineId: string, drafts: readonly AllocationDraft[], note: string | null, learnIban: boolean | null): Promise<AllocateResult> {
  return api.rpc<AllocateResult>("bank_line_allocate", {
    p_line: lineId,
    p_allocations: drafts.map((d) => ({ invoice_id: d.invoiceId, amount_cents: d.amountCents, write_off_rest: !!d.writeOffRest, write_off_reason: d.writeOffRest ? (d.writeOffReason ?? "").trim() : null })),
    p_note: note?.trim() || null,
    p_learn_iban: learnIban,
  });
}

export function ignoreLine(api: Api, lineId: string, reason: string, invoiceId: string | null): Promise<unknown> {
  return api.rpc("bank_line_ignore", { p_line: lineId, p_reason: reason.trim(), p_invoice: invoiceId });
}

export function reopenLine(api: Api, lineId: string, reason: string): Promise<unknown> {
  return api.rpc("bank_line_reopen", { p_line: lineId, p_reason: reason.trim() });
}

/** Konto hinterlegen: die IBAN geht nur flüchtig mit, die Datenbank speichert Maske und Prüfwert. */
export function addPayerAccount(api: Api, brandId: string, iban: string, holder: string | null): Promise<string> {
  return api.rpc<string>("brand_payer_account_add", { p_brand: brandId, p_iban: iban, p_holder: holder?.trim() || null });
}

export function forgetPayerAccount(api: Api, id: string): Promise<void> {
  return api.remove("brand_payer_accounts", [["id", "eq", id]]);
}

export function saveBankSettings(api: Api, orgId: string, patch: { bank_learn_payer_iban: boolean; bank_line_retention_days: number | null }): Promise<unknown> {
  return api.update("org_settings", [["org_id", "eq", orgId]], patch, { columns: "org_id" });
}
